/* eslint-disable react-hooks/exhaustive-deps */

/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { ACCESS_TOKEN, REFRESH_TOKEN } from '../constants';
// eslint-disable-next-line import/no-cycle
import useFetch from '../hooks/useFetch';
import { CurrentUser } from '../models';
import { CustomCheckboxInterface, CustomInputInterface } from '../models/inputs';
import { useAlert } from './alert.context';

interface UserInterface {
  user: CurrentUser;
  loginLoading: boolean;
  loginError: any;
  setUser: any;
  login: (
    email: CustomInputInterface,
    password: CustomInputInterface,
    remember: CustomCheckboxInterface,
  ) => Promise<void>;
  logout: () => void;
  forceUpdate: () => void;
  updateToken: (accessToken: string, refreshToken: string) => void;
  checkIfSSO: (email: CustomInputInterface) => Promise<null | {
    force_sso: boolean;
    sso_url?: string;
  }>;
  ssoCheckLoading: boolean;
}

const UserContext = createContext<UserInterface | null>(null);

export const useUserContext = () => useContext(UserContext) as UserInterface;

interface Props {
  children: JSX.Element;
}

export function UserContextProvider({ children }: Props) {
  const navigate = useNavigate();
  const {
    loading: loginLoading,
    doFetch: doLoginFetch,
    error: loginError,
  } = useFetch(`${process.env.REACT_APP_API_URL}/user/login`);
  const {
    doFetch: doUserFetch,
    error: userError,
    abortController,
  } = useFetch(
    `${process.env.REACT_APP_API_URL}/user?populate=profile_pic&populate=subscriptions&populate=user_groups`,
    { skipFailAlert: true },
  );
  const { doFetch: doSSOCheck, loading: ssoCheckLoading } = useFetch(
    `${process.env.REACT_APP_API_URL}/sso/info`,
  );
  const [storedAccessToken, setStoredAccessToken] = useState(() => {
    const localToken = localStorage.getItem(ACCESS_TOKEN);
    const sessionToken = sessionStorage.getItem(ACCESS_TOKEN);

    if (!localToken && !sessionToken) return '';
    if (localToken) return localToken;
    if (sessionToken) return sessionToken;
  });
  const [storedRefreshToken, setStoredRefreshToken] = useState(() => {
    const localToken = localStorage.getItem(REFRESH_TOKEN);
    const sessionToken = sessionStorage.getItem(REFRESH_TOKEN);

    if (!localToken && !sessionToken) return '';
    if (localToken) return localToken;
    if (sessionToken) return sessionToken;
  });
  const [user, setUser] = useState<CurrentUser>({
    email: '',
    token: storedAccessToken || '',
    role: 0,
    _id: '',
    subscriptions: [],
  });

  const { setAlert } = useAlert();
  const [fromLogin, setFromLogin] = useState(false);
  const [update, forceUpdate] = useReducer((x) => x + 1, 0);

  const setSessionStorageToken = useCallback((accessToken: string, refreshToken: string) => {
    sessionStorage.setItem(ACCESS_TOKEN, accessToken);
    setStoredAccessToken(accessToken);
    sessionStorage.setItem(REFRESH_TOKEN, refreshToken);
    setStoredRefreshToken(refreshToken);
  }, []);

  const setLocalSessionToken = useCallback((accessToken: string, refreshToken: string) => {
    localStorage.setItem(ACCESS_TOKEN, accessToken);
    setStoredAccessToken(accessToken);
    localStorage.setItem(REFRESH_TOKEN, refreshToken);
    setStoredRefreshToken(refreshToken);
  }, []);

  const removeAuthToken = useCallback(() => {
    sessionStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(ACCESS_TOKEN);
    sessionStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    setStoredAccessToken('');
    setStoredRefreshToken('');
    setUser({
      email: '',
      token: '',
      role: 0,
      _id: '',
      subscriptions: [],
    });
    navigate('/');
  }, [navigate]);

  const updateToken = useCallback(
    (accessToken: string, refreshToken: string) => {
      const localToken = localStorage.getItem(ACCESS_TOKEN) || localStorage.getItem(REFRESH_TOKEN);
      const sessionToken =
        sessionStorage.getItem(ACCESS_TOKEN) || sessionStorage.getItem(REFRESH_TOKEN);

      if (!localToken && !sessionToken) return;
      if (localToken) return setLocalSessionToken(accessToken, refreshToken);
      if (sessionToken) return setSessionStorageToken(accessToken, refreshToken);
    },
    [setLocalSessionToken, setSessionStorageToken],
  );

  useEffect(() => {
    const handleStorageUpdate = (e: any) => {
      const { key, newValue } = e;

      if (key === ACCESS_TOKEN) {
        if (newValue) {
          sessionStorage.setItem(ACCESS_TOKEN, (newValue || '').replaceAll('"', ''));
          setStoredAccessToken((newValue || '').replaceAll('"', ''));
        } else {
          sessionStorage.removeItem(ACCESS_TOKEN);
          setStoredAccessToken('');
        }
      }

      if (key === REFRESH_TOKEN) {
        if (newValue) {
          sessionStorage.setItem(REFRESH_TOKEN, (newValue || '').replaceAll('"', ''));
          setStoredRefreshToken((newValue || '').replaceAll('"', ''));
        } else {
          sessionStorage.removeItem(REFRESH_TOKEN);
          setStoredRefreshToken('');
        }
      }
    };

    window.addEventListener('storage', handleStorageUpdate);
    return () => {
      window.removeEventListener('storage', handleStorageUpdate);
    };
  }, [removeAuthToken, setLocalSessionToken]);

  useEffect(() => {
    if ((!storedAccessToken && !storedRefreshToken) || fromLogin) return;
    (async () => {
      const res = await Promise.all([
        doUserFetch({
          headers: {
            Authorization: `Bearer ${storedAccessToken}`,
          },
        }),
      ]);

      if (res[0] === 'aborted' || !res[0]) return;
      if (res[0]?.data) {
        const localToken = localStorage.getItem(ACCESS_TOKEN);
        const sessionToken = sessionStorage.getItem(ACCESS_TOKEN);

        const token = localToken || sessionToken || '';

        setUser((prevState) => ({
          ...prevState,
          ...res[0].data,
          token: token,
        }));
      }
    })();

    return () => {
      abortController.current?.abort();
    };
  }, [abortController, storedAccessToken, storedRefreshToken, doUserFetch, update, fromLogin]);

  const login = useCallback(
    async (
      email: CustomInputInterface,
      password: CustomInputInterface,
      remember: CustomCheckboxInterface,
    ) => {
      const res = await doLoginFetch({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.value,
          password: password.value,
          source: 'WORLD',
        }),
      });
      if (res?.data.accessToken && res?.data.refreshToken) {
        setAlert({
          message: res.message,
        });
        setFromLogin(true);
        if (remember.checked) {
          setLocalSessionToken(res.data.accessToken, res.data.refreshToken);
        } else {
          setSessionStorageToken(res.data.accessToken, res.data.refreshToken);
        }
        setTimeout(() => {
          window.location.replace(`/pg${window.location.search}`);
        }, 0);
      }
    },
    [doLoginFetch, setAlert, setLocalSessionToken, setSessionStorageToken],
  );

  const logout = useCallback(async () => {
    if (storedAccessToken) {
      removeAuthToken();
    }
  }, [storedAccessToken, removeAuthToken]);

  const checkIfSSO = useCallback(async (email: CustomInputInterface) => {
    const res = await doSSOCheck({
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email.value,
      }),
    });

    return !res ? null : res.data;
  }, []);

  useEffect(() => {
    if (!userError) return;
    abortController.current.abort();
    logout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userError]);

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      setUser,
      forceUpdate,
      loginLoading,
      loginError,
      userError,
      updateToken,
      checkIfSSO,
      ssoCheckLoading,
    }),
    [
      user,
      login,
      logout,
      loginLoading,
      loginError,
      userError,
      updateToken,
      checkIfSSO,
      ssoCheckLoading,
    ],
  );
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
